import { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Card, Heading, Icon, Paragraph } from '@zillow/constellation-10';
import { IconFileFilled } from '@zillow/constellation-icons';
import { formatRequirement, REQUIRED_DOCUMENT_COPY } from '@zg-rentals/applications-components';
import type { Answers, Document } from '../../../../models/rental-application';
import type { FeatureFlags } from '../../../../contexts/ApplicationContext';
import { useApplicationContext } from '../../../../contexts/ApplicationContext';
import { useIncomeDocsRedaction } from '../../../../hooks/useIncomeDocsRedaction';
import { PendingRemovalsContext, usePendingDocumentRemovals } from '../../../../hooks/usePendingDocumentRemovals';
import type { IncomeSourceGroup, RequestedRow } from '../../../../utils/incomeDocuments';
import { getIncomeDocumentsModel } from '../../../../utils/incomeDocuments';
import { INCOME_DOC_ENCOURAGEMENT_TEXT } from '../../../../constants/incomeDocs';
import { DocumentContainer } from './IncomeDocuments/DocumentContainer';
import { DocumentGroup } from './IncomeDocuments/DocumentGroup';
import { RequirementsProgress } from './IncomeDocuments/RequirementsProgress';

function useDocumentDetails(documents: Array<Document>, onExpired: () => void) {
  const { documents: details, areDocumentsExpired } = useIncomeDocsRedaction(documents);
  const withDetails = (rowDocuments: Array<Document>) =>
    rowDocuments.map((document) => details?.find((detail) => detail.id === document.id) ?? document);

  useEffect(() => {
    if (areDocumentsExpired) {
      onExpired();
    }
  }, [areDocumentsExpired, onExpired]);

  return withDetails;
}

function RequestedRowContainer({
  row,
  tag,
  isRequested,
  documents,
}: {
  row: RequestedRow;
  tag?: string;
  isRequested: boolean;
  documents: Array<Document>;
}) {
  const requirement = formatRequirement(
    row.section.parameterQuantity ?? undefined,
    row.section.parameterUnit ?? undefined,
  );
  const removeLabel = !isRequested ? 'Remove' : row.section.incomeId ? 'Remove from this section' : undefined;

  return (
    <DocumentContainer
      heading={REQUIRED_DOCUMENT_COPY[row.documentType].label}
      tag={tag}
      requirement={requirement || undefined}
      isRequested={isRequested}
      documents={documents}
      removal={removeLabel ? { label: removeLabel, section: row.section } : undefined}
      canDelete={isRequested}
    />
  );
}

function IncomeSourceDocuments({
  group,
  isTemplate,
  onExpired,
}: {
  group: IncomeSourceGroup;
  isTemplate: boolean;
  onExpired: () => void;
}) {
  const withDetails = useDocumentDetails(
    [...group.requestedRows.flatMap((row) => row.documents), ...group.supportingDocuments],
    onExpired,
  );

  return (
    <DocumentGroup
      title={group.label}
      description={!isTemplate && group.income.type ? INCOME_DOC_ENCOURAGEMENT_TEXT[group.income.type] : undefined}
    >
      {group.requestedRows.map((row) => (
        <RequestedRowContainer key={row.documentType} row={row} isRequested documents={withDetails(row.documents)} />
      ))}
      <DocumentContainer
        heading={isTemplate ? 'Supporting documents' : 'Income documents'}
        tag={isTemplate ? 'Optional' : undefined}
        documents={withDetails(group.supportingDocuments)}
        canDelete
      />
    </DocumentGroup>
  );
}

function RowsGroup({
  title,
  description,
  rows,
  isRequested,
  getTag,
  onExpired,
}: {
  title: string;
  description?: string;
  rows: Array<RequestedRow>;
  isRequested: boolean;
  getTag?: (row: RequestedRow) => string | undefined;
  onExpired: () => void;
}) {
  const withDetails = useDocumentDetails(
    rows.flatMap((row) => row.documents),
    onExpired,
  );

  return (
    <DocumentGroup title={title} description={description}>
      {rows.map((row) => (
        <RequestedRowContainer
          key={`${row.documentType}-${row.section.incomeId ?? 'application'}`}
          row={row}
          tag={getTag?.(row)}
          isRequested={isRequested}
          documents={withDetails(row.documents)}
        />
      ))}
    </DocumentGroup>
  );
}

export function RequiredDocumentsStep() {
  const { application, featureFlags, listing } = useApplicationContext();
  const isTemplateEligible = featureFlags.hasRequiredDocuments === true;
  const removals = usePendingDocumentRemovals(application.id as string, application.answers);
  const model = useMemo(
    () => getIncomeDocumentsModel(removals.answers, { isTemplateEligible }),
    [removals.answers, isTemplateEligible],
  );
  const isTemplate = model.mode === 'template';
  const requesterName = listing.listingOwner?.name?.trim() || 'The rental manager';
  const [hasExpiredDocuments, setHasExpiredDocuments] = useState(false);
  const onExpired = useCallback(() => setHasExpiredDocuments(true), []);
  const sourceLabels = new Map(
    model.incomeSources.flatMap(({ income, label }) => (income.id ? [[income.id, label]] : [])),
  );

  return (
    <PendingRemovalsContext.Provider value={removals.actions}>
      <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default', fontFamily: 'heading-alt' }}>
        Income documents
      </Heading>
      <Paragraph css={{ marginBottom: 'default', overflowWrap: 'anywhere' }}>
        {isTemplate
          ? `${requesterName} asked for these documents to support your application. You can submit without them, but they help your application get reviewed faster.`
          : 'Add documents that support each income source. They help your application get reviewed faster.'}
      </Paragraph>
      <Box css={{ display: 'flex', flexDirection: 'column', gap: 'default' }}>
        {hasExpiredDocuments && (
          <Alert tone="warning">
            Some income documents you added before have expired. Add the latest documents for each income source.
          </Alert>
        )}
        {isTemplate && <RequirementsProgress covered={model.coveredCount} total={model.requestedCount} />}
        {model.incomeSources.length === 0 && (
          <Alert tone="info">Add an income source on the Current income step to add documents for it.</Alert>
        )}
        {model.incomeSources.map((group) => (
          <IncomeSourceDocuments
            key={group.income.id ?? group.index}
            group={group}
            isTemplate={isTemplate}
            onExpired={onExpired}
          />
        ))}
        {model.applicationRows.length > 0 && (
          <RowsGroup
            title="Additional financial documents"
            rows={model.applicationRows}
            isRequested
            onExpired={onExpired}
          />
        )}
        {isTemplate && model.noLongerRequested.length > 0 && (
          <RowsGroup
            title="No longer requested"
            description="These files were added for a request that no longer applies to your income sources. They are still saved to your account."
            rows={model.noLongerRequested}
            isRequested={false}
            onExpired={onExpired}
            getTag={(row) => sourceLabels.get(row.section.incomeId ?? '')}
          />
        )}
      </Box>
      <Paragraph textStyle="body-sm" color="text.subtle" css={{ marginTop: 'default' }}>
        Sensitive information, like your SSN, may be automatically redacted when you upload a document. Preview your
        documents to verify that they do not include sensitive information. Password-protected and encrypted documents
        are not supported.
      </Paragraph>
    </PendingRemovalsContext.Provider>
  );
}

export function RequiredDocumentsStepTip() {
  return (
    <Card
      elevated={false}
      tone="neutral"
      body={
        <>
          <Icon size="xl" css={{ marginBottom: 'default' }}>
            <IconFileFilled />
          </Icon>
          <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
            Your documents
          </Heading>
          <Paragraph>
            Documents that support your income help rental managers verify it. The documents you add are saved to your
            account.
          </Paragraph>
        </>
      }
    />
  );
}

export const RequiredDocumentsRecommendation = {
  condition: ({ answers, featureFlags }: { answers: Answers; featureFlags?: FeatureFlags }) => {
    const { coveredCount, requestedCount } = getIncomeDocumentsModel(answers, {
      isTemplateEligible: featureFlags?.hasRequiredDocuments === true,
    });
    return coveredCount < requestedCount;
  },
  title: 'Income documents',
  body: (
    <Paragraph>
      The rental manager asked for documents that your application is still missing. You can submit without them, but
      adding them helps the rental manager decide faster.
    </Paragraph>
  ),
};
